import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  Page as PageLayout,
  PageHeader,
  Card,
  CardContent,
  Flex,
  Text,
  Heading,
  LoadingOrbit,
  Spinner,
  Button,
  useToast,
  DataTable,
  TextField,
  Select,
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogFooter,
  TagBadge,
  TAG_COLOR_OPTIONS,
  type SelectOption,
  type TagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { ArrowSquareOut, FileText, Trash } from '@phosphor-icons/react';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import { useAuth } from '../../contexts/AuthContext';
import { usePropertyBreadcrumbs } from '../../hooks/usePropertyBreadcrumbs';
import { resolveScreenshotSrc } from '../utils/resolveScreenshotSrc';
import { PageSkillPanel } from '../../features/page-skills';
import { ElementTaxonomyCard } from './page-detail/components/ElementTaxonomyCard';
import { usePropertyCredentialSummaries } from '../../hooks/usePropertyMetadata';
import { QaReportDrawer } from './components/QaReportDrawer';
import { CredentialSecretPopover } from '../components/issues/CredentialSecretPopover';
import { ElementTestTimeline, type ElementTest } from './components/ElementTestTimeline';
import { useAttachPageToLatestQaRun, useContinueQaReport } from '../../features/qa-runs/useQaRuns';
import { SuperadminOnly } from '../components/SuperadminOnly';
import {
  buildQaBarChartData,
  formatDate,
  formatRelativeTime,
  formatRelativeTimeAgo,
  QaActivityChart,
  QaReportCard,
  renderElementTypeBadge,
} from './page-detail/pageDetailUi';
import { useElementHints, type ElementHint } from './page-detail/useElementHints';
import { fetchCanonicalPageDetail } from './page-detail/api';
import {
  type CanonicalPageDetailResponse,
  type QaReportListResponse,
  type QaReportSummary,
  TAXONOMY_TYPE_OPTIONS,
  type TaxonomyElement,
} from './page-detail/types';

const QA_TIMELINE_PAGE_SIZE = 10;

const CATEGORY_LABELS: Record<ElementHint['category'], string> = {
  element: 'Element',
  format: 'Format',
  pattern: 'Pattern',
  guidance: 'Guidance',
};

function ElementHintsSection({ hints, isLoading }: { hints: ElementHint[]; isLoading: boolean }) {
  if (isLoading) {
    return null;
  }

  if (hints.length === 0) {
    return (
      <Card variant="default" padding="lg">
        <CardContent>
          <Heading size="sm">Element hints</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            No element hints captured yet for this page.
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="lg">
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Heading size="sm">Element hints</Heading>
          <Text size="sm" className="text-muted-foreground">
            Agent-learned knowledge about elements on this page.
          </Text>
        </div>
        <div className="space-y-2">
          {hints.map((hint) => (
            <div key={hint.id} className="flex items-start gap-3 rounded-lg border p-3">
              <TagBadge
                name={CATEGORY_LABELS[hint.category]}
                color={
                  hint.category === 'element'
                    ? 'blue'
                    : hint.category === 'format'
                      ? 'purple'
                      : hint.category === 'pattern'
                        ? 'green'
                        : 'gray'
                }
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <Text weight="medium" className="truncate">
                  {hint.element}
                </Text>
                <Text size="sm" className="text-muted-foreground line-clamp-2 mt-0.5">
                  {hint.frontmatter}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function PageDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams({ from: '/pages/$propertyId/canonical/$canonicalPageId' });
  const propertyIdParam = params.propertyId;
  const canonicalPageId = params.canonicalPageId;

  const hasPageTester = !!user?.features?.qaRuns;

  const [selectedCredentialKey, setSelectedCredentialKey] = React.useState<string>('public');
  const selectionInitializedRef = React.useRef(false);

  const canonicalPageQuery = useQuery({
    queryKey: ['canonical-page-detail', propertyIdParam, canonicalPageId, selectedCredentialKey],
    queryFn: async (): Promise<CanonicalPageDetailResponse> => {
      return fetchCanonicalPageDetail({
        apiUrl: API_URL,
        propertyId: propertyIdParam,
        canonicalPageId,
        credentialKey: selectedCredentialKey,
      });
    },
    enabled: !!propertyIdParam && !!canonicalPageId,
    placeholderData: keepPreviousData,
  });

  const canonicalPage = canonicalPageQuery.data?.canonicalPage;
  const taxonomyElements = canonicalPageQuery.data?.latestElements ?? [];

  const elementHintsQuery = useElementHints(propertyIdParam, canonicalPageId);

  const detailBreadcrumb = React.useMemo(() => {
    const title = canonicalPage?.title;
    if (!title && !canonicalPageId) {
      return undefined;
    }
    return [
      {
        key: `canonical-page-${canonicalPageId}`,
        label: title || 'Page Detail',
      },
    ];
  }, [canonicalPageId, canonicalPage?.title]);

  const { toast } = useToast();
  const [qaDrawerReport, setQaDrawerReport] = React.useState<QaReportSummary | null>(null);
  const [timelinePage, setTimelinePage] = React.useState(1);
  const [selectedQaReportId, setSelectedQaReportId] = React.useState<string | null>(null);
  const [taxonomyTypeFilter, setTaxonomyTypeFilter] = React.useState<SelectOption['id']>('all');
  const [taxonomySearch, setTaxonomySearch] = React.useState('');
  const [activeScreenshotUrl, setActiveScreenshotUrl] = React.useState<string | null>(null);
  const propertyCredentialsQuery = usePropertyCredentialSummaries(propertyIdParam, {
    enabled: hasPageTester && !!propertyIdParam,
  });

  const propertyContext = usePropertyBreadcrumbs({
    pageBreadcrumb: {
      key: 'pages-root',
      label: 'Pages',
      onPress: () =>
        navigate({
          to: propertyIdParam ? '/pages/$propertyId' : '/pages',
          params: propertyIdParam ? { propertyId: propertyIdParam } : undefined,
        }),
    },
    extraItems: detailBreadcrumb,
    onSelectProperty: (nextPropertyId) => {
      navigate({ to: '/pages/$propertyId', params: { propertyId: nextPropertyId } });
    },
    propertyBreadcrumbVariant: 'label',
  });
  const { propertyId, selectProperty, propertiesQuery } = propertyContext;

  React.useEffect(() => {
    if (!propertyIdParam || !propertiesQuery.data) {
      return;
    }
    const items = propertiesQuery.data.items ?? [];
    const validIds = new Set(items.map((item) => item.id));
    if (!validIds.has(propertyIdParam)) {
      navigate({ to: '/pages', replace: true });
      return;
    }
    if (propertyId !== propertyIdParam) {
      selectProperty(propertyIdParam);
    }
  }, [navigate, propertyId, propertyIdParam, propertiesQuery.data, selectProperty]);

  React.useEffect(() => {
    setTimelinePage(1);
    setSelectedQaReportId(null);
    setQaDrawerReport(null);
    setSelectedCredentialKey('public');
    selectionInitializedRef.current = false;
  }, [canonicalPageId]);

  React.useEffect(() => {
    if (!canonicalPage || selectionInitializedRef.current) {
      return;
    }
    const hasPublicCrawl = Boolean(canonicalPageQuery.data?.hasPublicCrawl);
    const knownCrawlCredentialIds = new Set(canonicalPageQuery.data?.crawlCredentialIds ?? []);
    const lastSeenCredentialId = canonicalPage.lastSeenCredentialId;
    if (lastSeenCredentialId && knownCrawlCredentialIds.has(lastSeenCredentialId)) {
      setSelectedCredentialKey(lastSeenCredentialId);
    } else if (hasPublicCrawl) {
      setSelectedCredentialKey('public');
    } else {
      const fallbackCredentialId = canonicalPageQuery.data?.crawlCredentialIds?.[0] ?? null;
      if (fallbackCredentialId) {
        setSelectedCredentialKey(fallbackCredentialId);
      }
    }
    selectionInitializedRef.current = true;
  }, [
    canonicalPage,
    canonicalPageQuery.data?.crawlCredentialIds,
    canonicalPageQuery.data?.hasPublicCrawl,
  ]);

  React.useEffect(() => {
    const hasPublicCrawl = Boolean(canonicalPageQuery.data?.hasPublicCrawl);
    const knownCrawlCredentialIds = new Set(canonicalPageQuery.data?.crawlCredentialIds ?? []);
    const isKnownCredential =
      selectedCredentialKey === 'public' || knownCrawlCredentialIds.has(selectedCredentialKey);
    if (isKnownCredential) {
      if (selectedCredentialKey === 'public' && !hasPublicCrawl) {
        const fallbackCredentialId = canonicalPageQuery.data?.crawlCredentialIds?.[0] ?? null;
        if (fallbackCredentialId) {
          setSelectedCredentialKey(fallbackCredentialId);
        }
      }
      return;
    }
    if (hasPublicCrawl) {
      setSelectedCredentialKey('public');
      return;
    }
    const fallbackCredentialId = canonicalPageQuery.data?.crawlCredentialIds?.[0] ?? null;
    if (fallbackCredentialId) {
      setSelectedCredentialKey(fallbackCredentialId);
    }
  }, [
    canonicalPageQuery.data?.crawlCredentialIds,
    canonicalPageQuery.data?.hasPublicCrawl,
    selectedCredentialKey,
  ]);

  const qaTimelineQuery = useQuery<QaReportListResponse>({
    queryKey: ['canonical-page-qa-reports', propertyIdParam, canonicalPageId, timelinePage],
    queryFn: async (): Promise<QaReportListResponse> => {
      if (!propertyIdParam || !canonicalPageId) {
        throw new Error('Missing identifiers');
      }
      const params = new URLSearchParams({
        page: String(timelinePage),
        pageSize: String(QA_TIMELINE_PAGE_SIZE),
      });
      const res = await fetch(
        `${API_URL}/properties/${propertyIdParam}/canonical-pages/${canonicalPageId}/qa-reports?${params}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw await toApiError(res, 'Failed to load QA history');
      }
      return (await res.json()) as QaReportListResponse;
    },
    enabled: hasPageTester && !!propertyIdParam && !!canonicalPageId,
    placeholderData: (previousData) => previousData,
  });

  const qaTestMutation = useMutation({
    mutationFn: async () => {
      if (!propertyIdParam || !canonicalPageId) {
        throw new Error('Missing page identifiers');
      }
      const credentialId = selectedCredentialKey === 'public' ? null : selectedCredentialKey;
      const credentialPayload = credentialId ? { credentialId } : {};
      const res = await fetch(
        `${API_URL}/properties/${propertyIdParam}/canonical-pages/${canonicalPageId}/qa-tests`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(credentialPayload),
        }
      );
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return (await res.json()) as { jobId: string };
    },
    onSuccess: () => {
      toast.success('QA agent started. Check diagnostics for progress.');
      setTimelinePage(1);
      setSelectedQaReportId(null);
      void canonicalPageQuery.refetch();
      void qaTimelineQuery.refetch();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to start QA agent.';
      toast.error(message);
    },
  });

  const attachToLatestRunMutation = useAttachPageToLatestQaRun(propertyIdParam);
  const continueQaReportMutation = useContinueQaReport();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const deletePageMutation = useMutation({
    mutationFn: async () => {
      if (!propertyIdParam || !canonicalPageId) {
        throw new Error('Missing page identifiers');
      }
      const res = await fetch(
        `${API_URL}/properties/${propertyIdParam}/canonical-pages/${canonicalPageId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return (await res.json()) as { ok: boolean };
    },
    onSuccess: () => {
      toast.success('Page deleted successfully.');
      setIsDeleteDialogOpen(false);
      // Navigate back to the pages list
      navigate({
        to: propertyIdParam ? '/pages/$propertyId' : '/pages',
        params: propertyIdParam ? { propertyId: propertyIdParam } : undefined,
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete page.';
      toast.error(message);
    },
  });

  const handleDeletePage = React.useCallback(() => {
    deletePageMutation.mutate();
  }, [deletePageMutation]);

  const handleRunQa = React.useCallback(() => {
    qaTestMutation.mutate();
  }, [qaTestMutation]);

  const handleRunQaAndAttach = React.useCallback(async () => {
    if (!propertyIdParam || !canonicalPageId) {
      toast.error('Missing page identifiers');
      return;
    }
    const credentialId = selectedCredentialKey === 'public' ? null : selectedCredentialKey;
    try {
      const result = await attachToLatestRunMutation.mutateAsync({
        canonicalPageId,
        credentialId,
      });
      const message = result.replacedReportId
        ? 'Replaced QA report in the latest run.'
        : 'Added page QA test to the latest run.';
      toast.success(message);
      setTimelinePage(1);
      setSelectedQaReportId(null);
      void canonicalPageQuery.refetch();
      void qaTimelineQuery.refetch();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add QA test to the latest run.';
      toast.error(message);
    }
  }, [
    attachToLatestRunMutation,
    canonicalPage?.lastSeenCredentialId,
    canonicalPageId,
    canonicalPageQuery,
    propertyIdParam,
    qaTimelineQuery,
    selectedCredentialKey,
    toast,
  ]);

  const handleContinueQaReport = React.useCallback(
    async (qaReportId?: string | null) => {
      if (!qaReportId) {
        toast.error('No QA report to continue.');
        return;
      }
      try {
        await continueQaReportMutation.mutateAsync(qaReportId);
        toast.success('Continuing QA agent with existing TODOs.');
        setTimelinePage(1);
        setSelectedQaReportId(qaReportId);
        void canonicalPageQuery.refetch();
        void qaTimelineQuery.refetch();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to continue QA run.';
        toast.error(message);
      }
    },
    [canonicalPageQuery, continueQaReportMutation, qaTimelineQuery, toast]
  );

  const credentialNameMap = React.useMemo(() => {
    const map = new Map<string, string | null>();
    const items = propertyCredentialsQuery.data?.items ?? [];
    items.forEach((item) => {
      if (item.id) {
        map.set(item.id, item.name ?? null);
      }
    });
    return map;
  }, [propertyCredentialsQuery.data]);
  const latestQaReport = canonicalPageQuery.data?.latestQaReport ?? null;
  const timelineReports = qaTimelineQuery.data?.reports ?? [];
  const totalTimelinePages = qaTimelineQuery.data?.pagination.totalPages ?? 0;
  const selectedCredentialId = selectedCredentialKey === 'public' ? null : selectedCredentialKey;
  const selectedCredentialName = selectedCredentialId
    ? (credentialNameMap.get(selectedCredentialId) ?? null)
    : null;
  const credentialAccessRestricted = propertyCredentialsQuery.data?.accessDenied ?? false;
  const credentialLabel = selectedCredentialId
    ? (selectedCredentialName ??
      (propertyCredentialsQuery.isFetching
        ? 'Loading credential…'
        : credentialAccessRestricted
          ? 'Restricted credential'
          : selectedCredentialId))
    : 'Public';

  React.useEffect(() => {
    if (!credentialAccessRestricted) {
      return;
    }
    const hasPublicCrawl = Boolean(canonicalPageQuery.data?.hasPublicCrawl);
    if (hasPublicCrawl && selectedCredentialKey !== 'public') {
      setSelectedCredentialKey('public');
    }
  }, [canonicalPageQuery.data?.hasPublicCrawl, credentialAccessRestricted, selectedCredentialKey]);

  React.useEffect(() => {
    if (totalTimelinePages > 0 && timelinePage > totalTimelinePages) {
      setTimelinePage(totalTimelinePages);
    }
  }, [totalTimelinePages, timelinePage]);

  const selectedCredentialTimelineReports = React.useMemo(() => {
    if (selectedCredentialId === null) {
      return timelineReports.filter((report) => !report.credentialId);
    }
    return timelineReports.filter((report) => report.credentialId === selectedCredentialId);
  }, [selectedCredentialId, timelineReports]);

  React.useEffect(() => {
    if (selectedCredentialTimelineReports.length > 0) {
      setSelectedQaReportId((current) => {
        if (current && selectedCredentialTimelineReports.some((report) => report.id === current)) {
          return current;
        }
        return selectedCredentialTimelineReports[0].id;
      });
    } else if (!qaTimelineQuery.isFetching) {
      setSelectedQaReportId(null);
    }
  }, [qaTimelineQuery.isFetching, selectedCredentialTimelineReports]);

  const selectedTimelineReport = timelineReports.find((report) => report.id === selectedQaReportId);
  const selectedTimelineReportMatchesCredential =
    selectedTimelineReport &&
    (selectedCredentialId === null
      ? !selectedTimelineReport.credentialId
      : selectedTimelineReport.credentialId === selectedCredentialId);
  const qaSummaryReport =
    (selectedTimelineReportMatchesCredential ? selectedTimelineReport : null) ??
    selectedCredentialTimelineReports[0] ??
    latestQaReport ??
    null;

  const qaBarChartData = React.useMemo(
    () => buildQaBarChartData(timelineReports),
    [timelineReports]
  );
  const contextQaReports = React.useMemo(() => {
    const reports: QaReportSummary[] = [];
    const seen = new Set<string>();
    const pushUnique = (report: QaReportSummary | null | undefined) => {
      if (report && !seen.has(report.id)) {
        reports.push(report);
        seen.add(report.id);
      }
    };
    selectedCredentialTimelineReports.forEach((r) => pushUnique(r));
    pushUnique(latestQaReport);
    return reports;
  }, [latestQaReport, selectedCredentialTimelineReports]);
  const isTemplateTestingEnabled = React.useMemo(
    () =>
      contextQaReports.some((report) =>
        report.todoItems?.some((todo) =>
          ['healthy', 'unhealthy', 'not_applicable'].includes(todo.status)
        )
      ),
    [contextQaReports]
  );
  const elementTestMap = React.useMemo(() => {
    const map = new Map<string, ElementTest[]>();
    if (!isTemplateTestingEnabled) return map;

    // Filter to only the selected report if one is selected
    const reportsToProcess = selectedQaReportId
      ? contextQaReports.filter((r) => r.id === selectedQaReportId)
      : contextQaReports;

    for (const report of reportsToProcess) {
      const completedAt = report.completedAt ?? report.startedAt ?? null;
      for (const todo of report.todoItems ?? []) {
        if (!todo.elementId) continue;
        const testName = todo.templateName ?? todo.item;
        const arr = map.get(todo.elementId) ?? [];
        arr.push({
          id: `${report.id}-${testName}-${arr.length}`,
          name: testName,
          status: todo.status,
          completedAt,
        });
        map.set(todo.elementId, arr);
      }
    }

    for (const [elementId, tests] of map.entries()) {
      const deduped = new Map<string, ElementTest>();
      for (const test of tests) {
        const existing = deduped.get(test.name);
        const testTime = test.completedAt ? new Date(test.completedAt).getTime() : 0;
        const existingTime = existing?.completedAt ? new Date(existing.completedAt).getTime() : -1;
        if (!existing || testTime >= existingTime) {
          deduped.set(test.name, test);
        }
      }
      const sorted = Array.from(deduped.values()).sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      });
      map.set(elementId, sorted.slice(0, 6));
    }

    return map;
  }, [contextQaReports, isTemplateTestingEnabled, selectedQaReportId]);
  const qaTimelineError = qaTimelineQuery.isError
    ? qaTimelineQuery.error instanceof Error
      ? qaTimelineQuery.error.message
      : 'Unable to load QA history'
    : null;
  const screenshotSrc = resolveScreenshotSrc(
    canonicalPageQuery.data?.latestCrawlPage?.screenshotS3Key ??
      canonicalPage?.latestScreenshotS3Key
  );

  const credentialColorMap = React.useMemo(() => {
    const colors = TAG_COLOR_OPTIONS.map((option) => option.id).filter((color) => color !== 'gray');
    const stableHash = (value: string) => {
      let hash = 2166136261;
      for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }
      return hash >>> 0;
    };

    const map = new Map<string, TagColor>();
    const credentials = propertyCredentialsQuery.data?.items ?? [];
    for (const credential of credentials) {
      if (!credential.id) continue;
      const idx = stableHash(credential.id) % colors.length;
      map.set(credential.id, colors[idx] ?? 'blue');
    }
    return map;
  }, [propertyCredentialsQuery.data?.items]);

  const credentialTags = React.useMemo(() => {
    const tags: Array<{
      key: string;
      label: string;
      credentialId: string | null;
      color: TagColor;
    }> = [];
    const hasPublicCrawl = Boolean(canonicalPageQuery.data?.hasPublicCrawl);
    if (hasPublicCrawl) {
      tags.push({ key: 'public', label: 'Public', credentialId: null, color: 'gray' });
    }
    if (credentialAccessRestricted) {
      return tags;
    }
    const knownCrawlCredentialIds = new Set(canonicalPageQuery.data?.crawlCredentialIds ?? []);
    const credentials = propertyCredentialsQuery.data?.items ?? [];
    for (const credential of credentials) {
      if (credential.id && knownCrawlCredentialIds.has(credential.id)) {
        tags.push({
          key: credential.id,
          label: credential.name ?? 'Credential',
          credentialId: credential.id,
          color: credentialColorMap.get(credential.id) ?? 'blue',
        });
      }
    }
    return tags;
  }, [
    canonicalPageQuery.data?.hasPublicCrawl,
    canonicalPageQuery.data?.crawlCredentialIds,
    credentialAccessRestricted,
    credentialColorMap,
    propertyCredentialsQuery.data?.items,
  ]);

  React.useEffect(() => {
    const leftMostKey = credentialTags[0]?.key ?? null;
    if (!leftMostKey) {
      return;
    }
    const isSelectedKeyVisible = credentialTags.some((tag) => tag.key === selectedCredentialKey);
    if (!isSelectedKeyVisible && selectedCredentialKey !== leftMostKey) {
      setSelectedCredentialKey(leftMostKey);
    }
  }, [credentialTags, selectedCredentialKey]);

  const handleSelectReportFromChart = React.useCallback(
    (reportId: string) => {
      setSelectedQaReportId(reportId);
      const report = timelineReports.find((r) => r.id === reportId);
      if (report) {
        if (!report.credentialId && !canonicalPageQuery.data?.hasPublicCrawl) {
          return;
        }
        setSelectedCredentialKey(report.credentialId ?? 'public');
      }
    },
    [canonicalPageQuery.data?.hasPublicCrawl, timelineReports]
  );
  const handleOpenQaReport = React.useCallback((report: QaReportSummary | null) => {
    if (report) {
      setQaDrawerReport(report);
    }
  }, []);
  const handleCloseQaReport = React.useCallback(() => {
    setQaDrawerReport(null);
  }, []);

  const filteredElements = React.useMemo(() => {
    const term = taxonomySearch.toLowerCase().trim();
    return taxonomyElements.filter((element) => {
      const matchesType = taxonomyTypeFilter === 'all' || element.type === taxonomyTypeFilter;
      const matchesSearch =
        !term ||
        element.label.toLowerCase().includes(term) ||
        (element.description ?? '').toLowerCase().includes(term);
      return matchesType && matchesSearch;
    });
  }, [taxonomyElements, taxonomySearch, taxonomyTypeFilter]);

  const taxonomyColumns = React.useMemo<ColumnDef<StockFeatures, TaxonomyElement, unknown>[]>(
    () => [
      {
        id: 'type',
        header: 'Type',
        meta: { flex: 1 },
        cell: ({ row }) => renderElementTypeBadge(row.original.type),
      },
      {
        id: 'label',
        header: 'Label',
        accessorFn: (row) => row.label,
        meta: { flex: 1.6 },
        cell: ({ row }) => (
          <Text weight="medium" className="truncate">
            {row.original.label}
          </Text>
        ),
      },
      {
        id: 'description',
        header: 'What it does',
        accessorFn: (row) => row.description ?? '',
        meta: { flex: 2.2 },
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground line-clamp-2">
            {row.original.description || '—'}
          </Text>
        ),
      },
      {
        id: 'qa',
        header: 'QA tests',
        meta: { flex: 1.4 },
        cell: ({ row }) =>
          isTemplateTestingEnabled ? (
            <ElementTestTimeline tests={elementTestMap.get(row.original.id) ?? []} />
          ) : (
            <Text size="sm" className="text-muted-foreground">
              —
            </Text>
          ),
      },
      {
        id: 'screenshot',
        header: 'Screenshot',
        meta: { flex: 1 },
        cell: ({ row }) =>
          row.original.screenshotUrl ? (
            <DialogTrigger size="4xl">
              <Button size="sm" variant="ghost">
                View
              </Button>
              <Dialog title={row.original.label || 'Element screenshot'}>
                <DialogContent className="p-0">
                  <div className="bg-muted/20 p-3">
                    <img
                      src={row.original.screenshotUrl}
                      alt={row.original.label ?? 'Element screenshot'}
                      className="rounded-lg border object-contain w-full max-h-[70vh]"
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </DialogTrigger>
          ) : (
            <Text size="sm" className="text-muted-foreground">
              —
            </Text>
          ),
      },
    ],
    [elementTestMap, isTemplateTestingEnabled]
  );

  const renderBody = () => {
    if (canonicalPageQuery.isLoading && !canonicalPageQuery.data) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      );
    }

    if ((canonicalPageQuery.isError && !canonicalPageQuery.data) || !canonicalPage) {
      return (
        <Card className="p-6 border-dashed border text-center">
          <Text weight="medium">Unable to load canonical page</Text>
          <Text size="sm" className="text-muted-foreground mt-1">
            Ensure the page exists and try again.
          </Text>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        <Card variant="default" padding="lg">
          <CardContent className="space-y-3">
            <Flex align="center" justify="between" wrap>
              <Heading size="sm">Latest screenshot</Heading>
              <Text size="sm" className="text-muted-foreground">
                {canonicalPageQuery.data?.latestCrawlPage?.createdAt
                  ? `Captured ${formatDate(canonicalPageQuery.data.latestCrawlPage.createdAt)}`
                  : `Last seen ${formatDate(canonicalPage.lastSeenAt)}`}
              </Text>
            </Flex>
            {screenshotSrc ? (
              <DialogTrigger
                isOpen={activeScreenshotUrl === screenshotSrc}
                onOpenChange={(open) => setActiveScreenshotUrl(open ? screenshotSrc : null)}
                size="4xl"
              >
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => setActiveScreenshotUrl(screenshotSrc)}
                >
                  <div className="bg-muted/20 p-3 transition hover:shadow-md">
                    <img
                      src={screenshotSrc}
                      alt="Latest page screenshot"
                      className="rounded-lg border object-cover w-full max-h-[520px] shadow-sm"
                    />
                  </div>
                </button>
                <Dialog title="Latest page screenshot">
                  <DialogContent className="p-0">
                    <div className="bg-muted/20 p-3">
                      <img
                        src={screenshotSrc}
                        alt="Latest page screenshot"
                        className="rounded-lg border object-contain w-full max-h-[70vh]"
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </DialogTrigger>
            ) : (
              <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
                No screenshot available yet. Run a crawl to capture this page.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent-authored knowledge about this page — both ungated (written on
            every fast-drive run), read together when auditing what the agent
            believes about the page. */}
        <div className="space-y-4">
          <Text size="xs" weight="medium" className="uppercase tracking-wide text-muted-foreground">
            What the agent knows about this page
          </Text>
          <ElementHintsSection
            hints={elementHintsQuery.data?.hints ?? []}
            isLoading={elementHintsQuery.isLoading}
          />
          {propertyIdParam && canonicalPage?.url && (
            <PageSkillPanel propertyId={propertyIdParam} pageUrl={canonicalPage.url} />
          )}
        </div>

        {hasPageTester && (
          <>
            <QaActivityChart
              chartData={qaBarChartData}
              selectedReportId={selectedQaReportId}
              onSelectReport={handleSelectReportFromChart}
              error={qaTimelineError}
            />

            <div className="space-y-2">
              <Flex align="center" gap="sm" wrap>
                <div className="flex flex-wrap items-center gap-2">
                  {credentialTags.map((tag) => {
                    const isSelected = selectedCredentialKey === tag.key;
                    return (
                      <button
                        key={tag.key}
                        type="button"
                        onClick={() => setSelectedCredentialKey(tag.key)}
                        aria-pressed={isSelected}
                        className={[
                          'rounded-full outline-none focus:outline-none transition-opacity',
                          isSelected ? 'hover:opacity-90' : 'opacity-70 hover:opacity-100',
                        ].join(' ')}
                      >
                        <TagBadge
                          name={tag.label}
                          color={isSelected ? tag.color : 'gray'}
                          size="md"
                        />
                      </button>
                    );
                  })}
                  {canonicalPageQuery.isFetching ? (
                    <Spinner size="xs" className="ml-1 self-center text-muted-foreground/70" />
                  ) : null}
                </div>
              </Flex>
              {credentialAccessRestricted ? (
                <Text size="sm" className="text-muted-foreground">
                  Credentials are restricted for your role; showing public context only.
                </Text>
              ) : null}
            </div>

            <QaReportCard
              qaReport={qaSummaryReport}
              onRunAgent={handleRunQa}
              onRunAgentWithLatestRun={handleRunQaAndAttach}
              onContinueRun={(reportId) => handleContinueQaReport(reportId)}
              onViewReport={handleOpenQaReport}
              isRunning={qaTestMutation.isPending}
              isAttaching={attachToLatestRunMutation.isPending}
              isContinuing={continueQaReportMutation.isPending}
            />

            <ElementTaxonomyCard
              taxonomyElements={taxonomyElements}
              filteredElements={filteredElements}
              taxonomyColumns={taxonomyColumns}
              taxonomySearch={taxonomySearch}
              onSearchChange={setTaxonomySearch}
              taxonomyTypeFilter={taxonomyTypeFilter}
              onTypeFilterChange={setTaxonomyTypeFilter}
              typeOptions={TAXONOMY_TYPE_OPTIONS}
            />
          </>
        )}

        <SuperadminOnly className="mt-8">
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="p-6">
              <Flex align="center" justify="between" wrap gap="md">
                <div className="space-y-1">
                  <Heading size="sm" className="text-destructive">
                    Danger zone
                  </Heading>
                  <Text size="sm" className="text-muted-foreground">
                    Permanently delete this page and all associated QA reports. This cannot be
                    undone.
                  </Text>
                </div>
                <Button
                  variant="destructive"
                  onPress={() => setIsDeleteDialogOpen(true)}
                  iconLeft={<Trash size={16} />}
                >
                  Delete page
                </Button>
              </Flex>
            </CardContent>
          </Card>
        </SuperadminOnly>
      </div>
    );
  };

  const headerDescription = canonicalPage ? (
    <div className="space-y-2">
      <Flex align="center" justify="between" gap="sm" wrap>
        <Flex align="center" gap="xs" wrap>
          <CredentialSecretPopover
            propertyId={propertyId ?? null}
            credentialId={selectedCredentialId}
            label={credentialLabel}
            canViewSecrets={true}
            isRestricted={credentialAccessRestricted}
          />
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground">
            {formatRelativeTime(canonicalPage.firstSeenAt)} old
          </Text>
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground">
            Last seen {formatRelativeTimeAgo(canonicalPage.lastSeenAt)}
          </Text>
        </Flex>
      </Flex>
      <Flex align="center" gap="sm" wrap>
        <Text className="text-muted-foreground break-all">{canonicalPage.url}</Text>
        <Button
          size="sm"
          variant="icon"
          iconOnly
          tooltip="Open in new tab"
          onPress={() => window.open(canonicalPage.url, '_blank', 'noopener')}
        >
          <ArrowSquareOut size={16} />
        </Button>
      </Flex>
    </div>
  ) : (
    'Review canonical page metadata and screenshots.'
  );

  return (
    <PageLayout>
      <div className="space-y-6">
        <PageHeader
          icon={<FileText size={24} />}
          title={canonicalPage?.title || 'Page Detail'}
          description={headerDescription}
        />
        {renderBody()}
      </div>
      {hasPageTester && (
        <QaReportDrawer
          report={qaDrawerReport}
          isOpen={!!qaDrawerReport}
          onClose={handleCloseQaReport}
          onRunAgent={handleRunQa}
          onRunAgentWithLatestRun={handleRunQaAndAttach}
          onContinueRun={handleContinueQaReport}
          isRunning={qaTestMutation.isPending}
          isAttaching={attachToLatestRunMutation.isPending}
          isContinuing={continueQaReportMutation.isPending}
          formatDate={formatDate}
          elements={taxonomyElements}
        />
      )}
      <DialogTrigger isOpen={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <span />
        <Dialog title="Delete page" description="Are you sure you want to delete this page?">
          <DialogContent>
            <Text>
              This will permanently delete the page{' '}
              <strong>{canonicalPage?.title || 'Untitled'}</strong> and all associated QA reports.
              This action cannot be undone.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onPress={handleDeletePage}
              isDisabled={deletePageMutation.isPending}
            >
              {deletePageMutation.isPending ? 'Deleting...' : 'Delete page'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </PageLayout>
  );
}
